import mixpanel from "mixpanel-browser";

const APP_NAME = "booking-portal";
const PII_KEYS = new Set([
	"name",
	"mobile",
	"phone",
	"email",
	"captcha",
	"captcha_id",
	"accesstoken",
	"token",
	"authorization",
]);

let isInitialized = false;

const isBrowser = () => typeof window !== "undefined";
const isEnabled = () =>
	process.env.REACT_APP_ANALYTICS_ENABLED === "true" &&
	!!process.env.REACT_APP_MIXPANEL_TOKEN &&
	isBrowser();

const isDebugEnabled = () => process.env.REACT_APP_MIXPANEL_DEBUG === "true";

const logDebug = (...args) => {
	if (isDebugEnabled()) {
		// eslint-disable-next-line no-console
		console.warn(...args);
	}
};

const buildBaseProperties = () => ({
	app_name: APP_NAME,
	environment: process.env.NODE_ENV || "development",
});

const normalizeValue = (value) => {
	if (value == null || value === "") {
		return undefined;
	}

	if (value instanceof Date) {
		return value.toISOString();
	}

	const valueType = typeof value;
	if (valueType === "string" || valueType === "number" || valueType === "boolean") {
		return value;
	}

	return undefined;
};

export const sanitizeProperties = (properties = {}) => {
	if (!properties || typeof properties !== "object") {
		return {};
	}

	return Object.entries(properties).reduce((acc, [key, value]) => {
		if (PII_KEYS.has(String(key).toLowerCase())) {
			return acc;
		}

		const normalized = normalizeValue(value);
		if (normalized !== undefined) {
			acc[key] = normalized;
		}

		return acc;
	}, {});
};

export const initAnalytics = () => {
	if (!isEnabled() || isInitialized) {
		return;
	}

	try {
		mixpanel.init(process.env.REACT_APP_MIXPANEL_TOKEN, {
			debug: isDebugEnabled(),
			track_pageview: false,
			persistence: "localStorage",
		});
		isInitialized = true;
	} catch (error) {
		logDebug("Mixpanel init failed", error);
	}
};

export const trackEvent = (eventName, properties = {}) => {
	if (!isEnabled()) {
		return;
	}

	try {
		if (!isInitialized) {
			initAnalytics();
		}

		if (!isInitialized) {
			return;
		}

		mixpanel.track(eventName, {
			...buildBaseProperties(),
			...sanitizeProperties(properties),
		});
	} catch (error) {
		logDebug(`Mixpanel track failed for ${eventName}`, error);
	}
};

export const trackPageView = (properties = {}) => {
	trackEvent("Page Viewed", properties);
};

export const identifyUser = (userId, properties = {}) => {
	if (!isEnabled() || !userId) {
		return;
	}

	try {
		if (!isInitialized) {
			initAnalytics();
		}

		if (!isInitialized) {
			return;
		}

		mixpanel.identify(userId);
		if (properties && Object.keys(properties).length > 0) {
			mixpanel.people.set(sanitizeProperties(properties));
		}
	} catch (error) {
		logDebug("Mixpanel identify failed", error);
	}
};

export const resetAnalytics = () => {
	if (!isEnabled() || !isInitialized) {
		return;
	}

	try {
		mixpanel.reset();
	} catch (error) {
		logDebug("Mixpanel reset failed", error);
	}
};

export const __analyticsInternals = {
	isEnabled,
	isDebugEnabled,
	buildBaseProperties,
	resetState: () => {
		isInitialized = false;
	},
};
